import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { projectsData } from '../data/projectsData';

const ProjectDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('overview');

    const project = projectsData.find(p => p.id === id);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [id]);

    if (!project) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Proje bulunamadı</h2>
                    <button
                        onClick={() => navigate('/')}
                        className="text-primary-500 hover:text-primary-600"
                    >
                        Ana sayfaya dön
                    </button>
                </div>
            </div>
        );
    }

    const tabs = [
        { id: 'overview', label: 'Genel Bakış' },
        { id: 'challenges', label: 'Zorluklar' },
        { id: 'solutions', label: 'Çözümler' },
        { id: 'features', label: 'Özellikler' }
    ];

    return (
        <div className="min-h-screen bg-gray-50 pt-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
                <button
                    onClick={() => navigate('/')}
                    className="flex items-center text-primary-500 hover:text-primary-600 mb-6 sm:mb-8 transition-colors duration-200"
                >
                    <ArrowLeftIcon className="h-5 w-5 mr-2" />
                    Geri Dön
                </button>

                <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                    <div className="relative h-64 sm:h-80 md:h-96 lg:h-[500px]">
                        <img
                            src={project.image}
                            alt={project.title}
                            className="w-full h-full object-contain bg-gray-100"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent flex items-end">
                            <div className="p-6 sm:p-8 text-white w-full">
                                <span className="bg-primary-500 text-white px-3 py-1 rounded-full text-sm font-medium mb-4 inline-block">
                                    {project.category}
                                </span>
                                <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-2">
                                    {project.title}
                                </h1>
                                <p className="text-lg sm:text-xl text-gray-200">
                                    {project.shortDescription}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                    <div className="flex flex-wrap border-b border-gray-200">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex-1 min-w-fit px-4 sm:px-6 py-3 sm:py-4 text-sm sm:text-base font-medium transition-colors duration-200 ${activeTab === tab.id
                                    ? 'bg-primary-500 text-white'
                                    : 'text-gray-700 hover:bg-gray-50'
                                    }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    <div className="p-6 sm:p-8 lg:p-12">
                        {activeTab === 'overview' && (
                            <div>
                                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
                                    {project.overview.title}
                                </h2>
                                <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-8">
                                    {project.overview.description}
                                </p>

                                {project.liveUrl && (
                                    <div className="mb-8 bg-gradient-to-r from-primary-50 to-primary-100 border-2 border-primary-200 rounded-xl p-6 sm:p-8">
                                        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                                            <div className="text-center sm:text-left">
                                                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
                                                    Projeyi Canlı Olarak İnceleyin
                                                </h3>
                                                <p className="text-sm sm:text-base text-gray-600">
                                                    {project.title} platformunu ziyaret ederek canlı olarak deneyimleyin
                                                </p>
                                            </div>
                                            <a
                                                href={project.liveUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 whitespace-nowrap"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                </svg>
                                                Siteyi Ziyaret Et
                                            </a>
                                        </div>
                                    </div>
                                )}

                                <div className="mb-8">
                                    <h3 className="text-xl font-semibold text-gray-900 mb-4">
                                        Kullanılan Teknolojiler
                                    </h3>
                                    <div className="flex flex-wrap gap-3">
                                        {project.technologies.map((tech, index) => (
                                            <span
                                                key={index}
                                                className="bg-primary-100 text-primary-700 px-4 py-2 rounded-lg font-medium"
                                            >
                                                {tech}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'challenges' && (
                            <div>
                                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
                                    {project.challenges.title}
                                </h2>
                                <ul className="space-y-4">
                                    {project.challenges.items.map((item, index) => (
                                        <li key={index} className="flex items-start">
                                            <div className="flex-shrink-0 w-2 h-2 bg-primary-500 rounded-full mt-2 mr-4"></div>
                                            <span className="text-base sm:text-lg text-gray-600 leading-relaxed">
                                                {item}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {activeTab === 'solutions' && (
                            <div>
                                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
                                    {project.solutions.title}
                                </h2>
                                <ul className="space-y-4">
                                    {project.solutions.items.map((item, index) => (
                                        <li key={index} className="flex items-start">
                                            <div className="flex-shrink-0 w-2 h-2 bg-primary-500 rounded-full mt-2 mr-4"></div>
                                            <span className="text-base sm:text-lg text-gray-600 leading-relaxed">
                                                {item}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {activeTab === 'features' && (
                            <div>
                                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
                                    {project.features.title}
                                </h2>
                                <ul className="space-y-4">
                                    {project.features.items.map((item, index) => (
                                        <li key={index} className="flex items-start">
                                            <div className="flex-shrink-0 w-2 h-2 bg-primary-500 rounded-full mt-2 mr-4"></div>
                                            <span className="text-base sm:text-lg text-gray-600 leading-relaxed">
                                                {item}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProjectDetail;

